import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Grid,
    TextField,
    Snackbar,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    Paper
} from '@mui/material';
import axios from '../../CustomAxios';
import Loading from '../components/Loading';
import { Box } from '@mui/system';

function UpdateProductionReport({ productionReport, jobDetails, setUp, up, onClose, setSuccess, showErrorAlert, setMessage, snackRef, apMachines }) {
    const [state, setState] = useState({
        jobData: { ...productionReport },
        plateDetails: null,
        maxValues: { maxBake: 0, maxCtp: 0 },
        totals: { totalBake: 0, totalCtp: 0 },
        error: '',
        success: false,
        loader: true,
        selectedMachine: apMachines.find(m => m._id === productionReport.ap_machine)
    });
    const [show, setShow] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        vertical: 'top',
        horizontal: 'center'
    });

    const [loading, setLoading] = useState(false);

    // Fetch plate details and max values
    useEffect(() => {
        const fetchPlateDetails = async () => {
            try {
                const authData = JSON.parse(localStorage.getItem('AuthData'));
                const config = {
                    headers: {
                        Authorization: "Bearer " + authData.access_token,
                    },
                };

                // 1. Get the specific plate from job's plates array
                const { data: jobData } = await axios.get(
                    `/api/v1/${authData.type}/jobs/${jobDetails._id}`,
                    config
                );

                const jobPlate = jobData.data.plates.find(
                    p => p?._id === productionReport?.unique_id
                );

                // 2. Get current totals from production reports
                const { data: totalsData } = await axios.get(
                    `/api/v1/${authData.type}/production_report/total/${jobDetails._id}/${productionReport.unique_id}`,
                    config
                );
                setState(prev => ({
                    ...prev,
                    plateDetails: jobPlate,
                    maxValues: {
                        maxBake: jobPlate?.bake,
                        maxCtp: jobPlate?.ctp_plate_count
                    },
                    totals: { totalBake: totalsData?.data?.bake || 0, totalCtp: totalsData?.data?.ctp || 0 },
                    loader: false
                }));
                setShow(true);
            } catch (error) {
                console.log("Error", error);
                setState(prev => ({
                    ...prev,
                    error: 'Failed to load plate details',
                    loader: false
                }));
                setShow(false);
                setSnackbar(prev => ({ ...prev, open: true }));
            }
        };

        fetchPlateDetails();
    }, [productionReport, jobDetails]);

    // Update handler with validation
    const handleSubmit = async () => {
        const { maxValues, jobData } = state;

        const isBakeUnchanged = Number(jobData.baking_quantity) === productionReport.baking_quantity;
        const isCtpUnchanged = Number(jobData.ctp_quantity) === productionReport.ctp_quantity;

        if (isBakeUnchanged && isCtpUnchanged) {
            setState(prev => ({ ...prev, error: 'No Change in Values' }));
            setSnackbar(prev => ({ ...prev, open: true }));
            return;
        }

        // Validate against max values
        if (jobData.baking_quantity > maxValues.maxBake) {
            setState(prev => ({ ...prev, error: 'Bake quantity exceeds maximum' }));
            setSnackbar(prev => ({ ...prev, open: true }));
            return;
        }

        if (jobData.ctp_quantity > maxValues.maxCtp) {
            setState(prev => ({ ...prev, error: 'CTP quantity exceeds maximum' }));
            setSnackbar(prev => ({ ...prev, open: true }));
            return;
        }


        try {
            setLoading(true);
            const authData = JSON.parse(localStorage.getItem('AuthData'));
            const config = {
                headers: {
                    Authorization: "Bearer " + authData.access_token,
                },
            };

            const updatedReport = {
                unique_id: jobData.unique_id,
                baking_quantity: jobData.baking_quantity,
                ctp_quantity: jobData.ctp_quantity,
                comment: jobData.comment,
                job_id: jobData.job_id._id,
                plate: jobData.plate._id,
                audit_logs: {
                    action_by: authData.user_id,
                    timestamp: new Date(),
                    action: "update_dpr",
                    description: `Updated values - Bake: ${jobData.baking_quantity}, CTP: ${jobData.ctp_quantity}`
                },
                ap_machine: state.selectedMachine._id
            };

            await axios.put(
                `/api/v1/${authData.type}/production_report/${productionReport._id}`,
                updatedReport,
                config
            );

            setUp(up + 1);
            setState(prev => ({
                ...prev,
                success: true,
                error: '',
                totals: {
                    totalBake: prev.totals.totalBake + (jobData.baking_quantity - productionReport.baking_quantity),
                    totalCtp: prev.totals.totalCtp + (jobData.ctp_quantity - productionReport.ctp_quantity)
                }
            }));
            setSuccess(true);
            snackRef.current.click();
            setMessage("Production Report Updated Successfully");
            setSnackbar(prev => ({ ...prev, open: true }));
            onClose();



        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error.response?.data?.error || 'Update failed',
                success: false
            }));
            setSnackbar(prev => ({ ...prev, open: true }));
            onClose();
            showErrorAlert("Production Report Update Failed");
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card mb={6} sx={{ width: "50vw" }}>
            <CardContent>
                <Paper elevation={3} style={{ padding: "2%", minHeight: "200px" }}>
                    <Typography variant="h5" color="primary" mb={6} gutterBottom>
                        Create  Production Report
                    </Typography>
                    {show && (
                        <Grid item xs={12} mb={4}>
                            <Alert mt={2} mb={4} severity="info">
                                {"Total CTP quantities added- " +
                                    state.totals.totalCtp +
                                    ", Total Bake Quantity added- " +
                                    state.totals.totalBake}
                            </Alert>
                        </Grid>
                    )}
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Snackbar
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open={snackbar.open}
                            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        >
                            <Alert severity={state.error ? 'error' : 'success'}>
                                {state.error || 'Production report updated successfully!'}
                            </Alert>
                        </Snackbar>

                        {
                            (state.loader) ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                    <CircularProgress />
                                </Box>
                            ) : state.plateDetails ? (
                                <>
                                    {/* Current Values */}
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            size='small'
                                            label="Job"
                                            value={productionReport.job_id.job_id}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            size='small'
                                            label="Plate"
                                            value={`${productionReport.plate.height}X${productionReport.plate.width}X${productionReport.plate.thickness}`}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>

                                    {/* Max Values */}
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            size='small'
                                            label="Max Allowed Bake"
                                            value={state.maxValues.maxBake}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            size='small'
                                            label="Max Allowed CTP"
                                            value={state.maxValues.maxCtp}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>

                                    {/* Input Fields */}
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            size='small'
                                            label="Bake Quantity"
                                            type="number"
                                            fullWidth
                                            value={state.jobData.baking_quantity}
                                            onChange={(e) => {

                                                if (e.target.value > state.maxValues.maxBake) {
                                                    setState(prev => ({
                                                        ...prev,
                                                        error: "Please enter a lower amount; you are exceeding the maximum bake quantity",
                                                        success: false
                                                    }));
                                                    setSnackbar(prev => ({ ...prev, open: true }));
                                                    return;
                                                }

                                                if (
                                                    parseInt(e.target.value) +
                                                    parseInt(state.totals.totalBake) - parseInt(productionReport.baking_quantity) >
                                                    parseInt(state.maxValues.maxBake)
                                                ) {
                                                    setState(prev => ({
                                                        ...prev,
                                                        error: "Cannot make entry as the total will exceed max bake quantity",
                                                        success: false
                                                    }));
                                                    setSnackbar(prev => ({ ...prev, open: true }));
                                                    return;
                                                }

                                                setState(prev => ({ ...prev, jobData: { ...prev.jobData, baking_quantity: e.target.value } }));
                                            }}

                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            size='small'
                                            label="CTP Quantity"
                                            type="number"
                                            fullWidth
                                            value={state.jobData.ctp_quantity}
                                            onChange={(e) => {

                                                if (e.target.value > state.maxValues.maxCtp) {
                                                    setState(prev => ({
                                                        ...prev,
                                                        error: "Please enter a lower amount; you are exceeding the maximum ctp quantity",
                                                        success: false
                                                    }));
                                                    setSnackbar(prev => ({ ...prev, open: true }));
                                                    return;
                                                }

                                                if (
                                                    parseInt(e.target.value) +
                                                    parseInt(state.totals.totalCtp) - parseInt(productionReport.ctp_quantity) >
                                                    parseInt(state.maxValues.maxCtp)
                                                ) {
                                                    setState(prev => ({
                                                        ...prev,
                                                        error: "Cannot make entry as the total will exceed max CTP quantity",
                                                        success: false
                                                    }));
                                                    setSnackbar(prev => ({ ...prev, open: true }));
                                                    return;
                                                }
                                                setState(prev => ({ ...prev, jobData: { ...prev.jobData, ctp_quantity: e.target.value } }));
                                            }}

                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            size='small'
                                            label="Comment"
                                            value={state.jobData.comment}
                                            fullWidth
                                            onChange={(e) => setState(prev => ({
                                                ...prev,
                                                jobData: {
                                                    ...prev.jobData,
                                                    comment: e.target.value
                                                }
                                            }))}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Autocomplete
                                            size="small"
                                            options={apMachines}
                                            getOptionLabel={(option) =>
                                                `${option.name}-${option.serial_no}`
                                            }
                                            value={state.selectedMachine}
                                            onChange={(e, newValue) => {
                                                setState(prev => ({
                                                    ...prev,
                                                    selectedMachine: newValue
                                                }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Machine"
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSubmit}
                                            disabled={loading}
                                            sx={{ mt: 2 }}
                                        >
                                            {loading ? "Updating..." : "Update Production Report"}
                                        </Button>
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12}>
                                    <Typography color="error">
                                        Error: Could not load plate details
                                    </Typography>
                                </Grid>
                            )}

                    </Grid>
                </Paper>
            </CardContent>
        </Card>

    );
}

export default UpdateProductionReport;